import { merge } from 'lodash';
import { ReactNode, createContext } from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import { Colors } from './colors';

export interface ColorTheme {
  accent: string;
  accentInactive: string;
  accentInactiveDeemphasized: string;
  grayBackground: string;
  error: {
    foreground: string;
    background: string;
    border: string;
  };
  highlightActive: string;
  highlightInactive: string;
  background: string;
  backgroundInactive: string;
  foregroundActive: string;
  foregroundInactive: string;
  listRow: {
    background: string;
  };
  input: {
    backgroundActive: string;
    backgroundInactive: string;
    foregroundActive: string;
    foregroundInactive: string;
    label: string;
    borderInactive: string;
    borderActive: string;
  };
  responsiveInput: {
    backgroundActive: string;
    backgroundInactive: string;
    foregroundActive: string;
    foregroundInactive: string;
    label: string;
    borderInactive: string;
    borderActive: string;
  };
  avatar: {
    background: string;
    foreground: string;
  };
  button: {
    foreground: string;
    foregroundInverted: string;
    background: string;
    backgroundInverted: string;
    destructive: string;
    tertiary: string;
  };
  dropdown: {
    backgroundNoSelection: string;
    backgroundWithSelection: string;
  };
  section: {
    border: string;
  };
}

export const defaultTheme: ColorTheme = {
  accent: Colors.v2.green,
  accentInactive: Colors.v2.green30,
  accentInactiveDeemphasized: Colors.v2.green30_opaque,
  grayBackground: Colors.v2.grayBackground,
  error: {
    foreground: Colors.v2.actionWarmRed,
    background: Colors.v2.actionTropicalRed10,
    border: Colors.v2.actionTropicalRed,
  },
  highlightActive: Colors.v2.green10,
  highlightInactive: Colors.v2.green10,
  background: Colors.v2.white,
  backgroundInactive: Colors.v2.gray05,
  foregroundActive: Colors.v2.purple,
  foregroundInactive: Colors.v2.gray50,
  listRow: {
    background: 'transparent',
  },
  input: {
    backgroundActive: Colors.v2.gray05,
    backgroundInactive: Colors.v2.gray05,
    foregroundActive: Colors.v2.purple,
    foregroundInactive: Colors.v2.gray50,
    label: Colors.v2.gray30,
    borderInactive: Colors.v2.gray10,
    borderActive: Colors.v2.green30,
  },
  responsiveInput: {
    backgroundActive: Colors.v2.gray05,
    backgroundInactive: Colors.v2.gray05,
    foregroundActive: Colors.v2.purple,
    foregroundInactive: Colors.v2.gray50,
    label: Colors.v2.purple,
    borderInactive: Colors.v2.gray10,
    borderActive: Colors.v2.green30,
  },
  avatar: {
    background: Colors.v2.purple50,
    foreground: Colors.v2.green,
  },
  button: {
    foreground: Colors.v2.white,
    foregroundInverted: Colors.v2.purple,
    background: Colors.v2.purple,
    backgroundInverted: Colors.v2.white,
    destructive: Colors.v2.actionWarmRed,
    tertiary: Colors.v2.gray05,
  },
  section: {
    border: Colors.v2.gray05,
  },
  dropdown: {
    backgroundNoSelection: Colors.v2.grayBackground,
    backgroundWithSelection: Colors.v2.white,
  },
};

export const darkTheme: ColorTheme = {
  accent: Colors.v2.green,
  accentInactive: Colors.v2.green30,
  accentInactiveDeemphasized: Colors.v2.green30_opaque,
  grayBackground: Colors.v2.grayBackground,
  error: {
    foreground: Colors.v2.actionWarmRed,
    background: Colors.v2.actionTropicalRed10,
    border: Colors.v2.gray10,
  },
  highlightActive: Colors.overlay.white20,
  highlightInactive: Colors.overlay.white10,
  background: Colors.v2.greenSecondary90,
  backgroundInactive: Colors.v2.greenSecondary90,
  foregroundActive: Colors.v2.white,
  foregroundInactive: Colors.v2.tertiarySlateGreen,
  listRow: {
    background: Colors.v2.greenSecondary90,
  },
  input: {
    backgroundActive: Colors.overlay.white90,
    backgroundInactive: Colors.overlay.white30,
    foregroundActive: Colors.v2.purple,
    foregroundInactive: Colors.v2.purple,
    label: Colors.overlay.white30,
    borderInactive: Colors.v2.gray10,
    borderActive: Colors.v2.green30,
  },
  responsiveInput: {
    backgroundActive: Colors.overlay.white90,
    backgroundInactive: Colors.overlay.white30,
    foregroundActive: Colors.v2.purple,
    foregroundInactive: Colors.v2.purple,
    label: Colors.v2.whiteAlpha50,
    borderInactive: Colors.v2.gray10,
    borderActive: Colors.v2.green30,
  },
  avatar: {
    background: Colors.overlay.white20,
    foreground: Colors.v2.green,
  },
  button: {
    foreground: Colors.v2.white,
    foregroundInverted: Colors.v2.white,
    background: Colors.overlay.white20,
    backgroundInverted: 'transparent',
    destructive: Colors.v2.actionWarmRed,
    tertiary: Colors.v2.gray05,
  },
  section: {
    border: Colors.v2.white,
  },
  dropdown: {
    backgroundNoSelection: Colors.v2.grayBackground,
    backgroundWithSelection: Colors.v2.white,
  },
};

export const grayTheme: ColorTheme = merge({}, defaultTheme, {
  background: Colors.v2.gray05,
  highlightActive: Colors.v2.gray10,
  highlightInactive: Colors.v2.gray10,
  listRow: {
    background: Colors.v2.gray05,
  },
});

export const ColorThemeContext = createContext<ColorTheme>(defaultTheme);

interface ColorThemeProviderProps {
  children: ReactNode;
}

export function LightColorTheme({ children }: ColorThemeProviderProps) {
  return (
    <div data-theme="light" style={{ display: 'contents' }}>
      <ColorThemeContext.Provider value={defaultTheme}>{children}</ColorThemeContext.Provider>
    </div>
  );
}

export function DarkColorTheme({ children }: ColorThemeProviderProps) {
  return (
    <div data-theme="dark" style={{ display: 'contents' }}>
      <ColorThemeContext.Provider value={darkTheme}>{children}</ColorThemeContext.Provider>
    </div>
  );
}

export function GrayColorTheme({ children }: ColorThemeProviderProps) {
  return (
    <div data-theme="gray" style={{ display: 'contents' }}>
      <ColorThemeContext.Provider value={grayTheme}>{children}</ColorThemeContext.Provider>
    </div>
  );
}

export const cursorPointerStyle = { cursor: 'pointer' } as StyleProp<ViewStyle>;
